import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function AdministrativeReports({ locationData, isSearchApplied }) {

  const [selectedAdministrativeForm, setSelectedAdministrativeForm] = useState('');

  // Mapping for API 'type' parameter based on administrative form name
  const administrativeFormToApiTypeMap = {
    'MDG Data Capture Form': 1,
    'Data Aggregation Form': 2,
    'BCPC Form 2': 3,
    'Registered Barangay Inhabitants': 4,
    'RBI Form A': 5, // RBI Form A added
    'RBI Form B': 6,
    'Barangay Governance Management System': 7,
    'Form No. CMP-03-A': 8,
    'Literacy Report Form': 9,
    'Information on education by age and sex': 10,
    'MHO Comprehensive Survey Form': 11
  };

  // Function to generate the PDF report for administrative forms
  const generateAdminReport = async (formName, reportLocationData) => { // Renamed locationData to reportLocationData for clarity
    const { regionName, provinceName, municipalName, barangayName} = reportLocationData;
    let doc;
    let fetchedData = {}; // Initialize as object for easier key access
    const apiType = administrativeFormToApiTypeMap[formName];
    let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/reports/admin`

    if (formName === 'MDG Data Capture Form') {
      doc = new jsPDF("landscape", "pt", "A4"); // Landscape for MDG

      // Specific logic for MDG Data Capture Form
      // Add text to upper left corner
      doc.setFontSize(10);
      doc.text('CITY/MUNICIPALITY MDG DATA CAPTURE FORM', 40, 40, { align: 'left' });

      // Create a 47 row, 10 column empty table
      const head = [
        'ROW NO.',
        'INDICATORS\n(1)',
        'BASE YEAR\n(2)',
        'YEAR 1 \n TARGET/\nBALANCE\n(3)',
        'ACTUAL\n(4)',
        'YEAR 2 \n TARGET/\nBALANCE\n(5)',
        'ACTUAL\n(6)',
        'YEAR 3 \n TARGET/\nBALANCE\n(7)',
        'ACTUAL\n(8)',
        'SOURCE OF DATA\n(9)'
      ];

      // Indicator texts for column 2
      const indicators = [
        'Total number of households',
        'Number of households living below the poverty threshold',
        'Number of households living below poverty threshold who are now raised from poverty threshold',
        'Number of households living below food threshold',
        'Number of households living below food threshold who are now raised from living below the food threshold',
        'Number of persons 15 years old and above',
        'Number of persons 15 years old and above who are unemployed/not working but actively seeking work',
        'Number of persons 15 years old and above who are unemployed/not working but actively seeking work, who are now employed/working',
        'Number of children 0 to 5 years old',
        'Number of children 0 to 5 years old who are moderately and severely underweight (below normal-low and below normal very low)',
        'Number of children 0 to 5 years old who are moderately and severely underweight (below normal-low and below normal very low), who are now not moderately and severely underweight',
        'Number of households who eat less than three full meals a day',
        'Number of households who eat less than three full meals a day, who are now eating three full meals a day',
        'Number of households with members victimized by crime within the municipality',
        'Number of households with members victimized by crime within the municipality, who are safe from crime',
        'Number of children aged 6 to 12 years old',
        'Number of children aged 6 to 12 years old who are not in elementary school',
        'Number of children aged 6 to 12 years old who are in elementary school',
        'Number of children aged 13 to 16 years',
        'Number of children aged 13 to 16 years who are not in secondary school',
        'Number of children aged 13 to 16 years who are not in secondary school, but now in secondary school',
        'Number of children/youth aged 13 to 20 years',
        'Number of children/youth aged 13 to 20 years who did not attend elementary school',
        'Number of children/youth aged 13 to 20 years who did not attend elementary school, who are now in elementary school',
        'Number of male and female aged 6 to 16 years',
        'Number of female aged 6 to 16 years who are not in elementary/secondary school',
        'Number of female aged 6 to 16 years who are not in elementary/secondary school, but now in elementary/secondary school',
        'Number of persons aged 17 to 21 years who are not enrolled in any post-secondary school',
        'Number of females aged 17-21 years who are not enrolled in any post-secondary school',
        'Number of females aged 17-21 years who are not enrolled in any post-secondary school but now enrolled',
        'Number of children under 5 years',
        'Number of children under 5 years who dies due to illness',
        'Number of children under 5 years safe from death cause by illness',
        'Number of children who are pregnant/gave birth',
        'Number of women who who died due to childbirth or other pregnancy-related causes',
        'Number of women who are safe from death due to pregnancy-related causes',
        'Number of households who do not have access to safe drinking water',
        'Number of households who do not have access to safe drinking water , who are now with access to safe drinking water',
        'Number of households who do not have sanitary toilet',
        'Number of households who do not have sanitary toilet, who are now have sanitary toilet',
        'Number of households who are informal settlers',
        'Number of households who are informal settlers, who have secured lot/housing tenure status',
        'Number of households living in makeshift housing',
        'Number of households living in makeshift housing, who are now living in safe dwelling units',
        'Number of households who have access to internet',
        'Number of households who do not have access to HIV/AIDS information',
        'Number of households who do not have access to HIV/AIDS information, who are now have access to HIV/AIDS information'
      ];

      // Initialize body with 47 rows, 10 empty columns
      const body = Array(47).fill(null).map((_, rowIndex) => {
        const row = Array(10).fill('');
        if (rowIndex < indicators.length) {
          row[0] = `(${rowIndex + 1})`; // Add (1), (2), ... to the first column
          row[1] = indicators[rowIndex]; // Add indicator text to the second column
        } else {
          row[0] = `(${rowIndex + 1})`; // Continue numbering for empty rows
        }
        return row;
      });

      autoTable(doc, {
        startY: 70,
        head: [head],
        body: body,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: 30, valign: 'middle', halign: 'center' },
          1: { cellWidth: 240, valign: 'middle', halign: 'left', minCellHeight: 20 },
          9: { cellWidth: 150 },
        },
      });

    } else if (formName === 'Data Aggregation Form') {
      doc = new jsPDF("portrait", "pt", "A4"); // Portrait for Data Aggregation Form

      // Add "Form 2: Data Aggregation Form"
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Form 2: Data Aggregation Form', 40, 40, { align: 'left' });

      // Add "Barangay: Sabang" (or dynamic barangayName)
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Barangay: ${barangayName}`, doc.internal.pageSize.width - 40, 40, { align: 'right' });

      // Fetch data from the /admin endpoint for type 2
      if (apiType) {
        try {
          const location = `${provinceName}_${municipalName}_${barangayName}`
          const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY
            }
          });

if (!response.ok) {
  const errorText = await response.text();
  throw new Error(`HTTP error! status: ${response.status}, Response: ${errorText}`);
}
const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            fetchedData = data[0]; // Assuming it returns an array with one object
          } else {
            console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
            // Default empty fetchedData if no data
          }
        } catch (error) {
          console.error(`Error fetching data for ${formName}:`, error);
          // Default empty fetchedData on error
        }
      }

      // Table head for Data Aggregation Form (empty as per image)
      const head = [['', '']];

      // Table body for Data Aggregation Form based on the image
      const body = [
        [{ content: 'Transparency and Accountability', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['1. Has the barangay posted all sixty (60) financial documents at the barangay hall that is visible to the public?**',''],
        ['2. Is the municipality compliant with the Full Disclosure Policy?', ''],
        ['3. How much local revenues collected?', ''],
        ['4. COA Opinion?', ''],
        [{ content: 'Clean Water, Energy, and Sanitation for All', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['5. How many HHs without access to potable water?', fetchedData.hh_no_potable_water || '0'],
        ['6. How many HHs without sanitary toilet?', fetchedData.hh_no_sanitary_toilet || '0'],
        ['7. How many HHs without electricity?', fetchedData.hh_no_electricity || '0'],
        [{ content: 'Inclusive and Equitable Education', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['8. How many children 6-12 years old are not attending elementary school (for SY 2015-2016)?', fetchedData.children_no_elem_school || '0'],
        ['9. How many children 12-15 years old are not attending high school (SY 2015-2016)?', fetchedData.children_no_high_school || '0'],
        [{ content: 'Good Health and Well-being', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['10. How many children aged 0-5 are malnourished?', fetchedData.children_malnourished || '0'],
        ['11. How many children aged 0-5 have not received any vaccine?', fetchedData.children_no_vaccine || '0'],
        ['12. How many women died due to pregnancy-related causes?', fetchedData.women_died_pregnancy || '0'],
        ['13. How many HHs are living in makeshift housing?', fetchedData.hh_makeshift_housing || '0'],
        ['14. How many HHs are informal settlers?', fetchedData.hh_informal_settlers || '0'],
        [{ content: 'Disaster Resilient Community', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['15. How many HHs without emergency kit (emergency ladder/emergency go bag)?',  ''],
        ['16. Are there signs/directions going to the evacuation center?',  ''],
        ['17. How many items are not yet contained in Operation Listo checklist?',  ''],
        [{ content: 'More Business and Investments', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['18. How long are unpaved brgy roads (in meters)?',  ''],
        ['19. How long are unpaved municipal roads (in kms)?',  ''],
        ['20. How long will it take to process new business permits?', ''],
        ['21. How long will it take to process renewal of business permits?', ''],
        [{ content: 'Peaceful, Orderly, and Just Community', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['22. How many complaints are filed in the Lupon/Tagapamayapa?',''],
        ['23. Out of the complaints, how many were settled by the Lupon/Tagapamayapa?',''],
        [{ content: 'Clean and Livable Environment', colSpan: 2, styles: { fontStyle: 'bold', halign: 'left', fillColor: [220, 220, 220] } }],
        ['24. Is there an access to material recovery facility in the barangay? (e.g., solid waste storage and recycling area)',''],
        ['25. Is the barangay practicing segregation?',''],
        ['26. Is there an access to sanitary landfill?',''],
        ['27. Is there a waste collection system?',''],
      ];

      autoTable(doc, {
        startY: 60, // Adjusted startY for this form
        head: head,
        body: body,
        theme: 'grid',
        styles: {
          fontSize: 10, // Increased font size by 2 (from 8 to 10)
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 10, // Increased font size by 2 (from 8 to 10)
        },
        columnStyles: {
          0: { cellWidth: 400, halign: 'left' }, // Wider for indicators
          1: { cellWidth: 100, halign: 'center' }, // Narrower for values
        },
      });

    } else if (formName === 'BCPC Form 2') {
      doc = new jsPDF("landscape", "pt", "A4"); // Landscape for BCPC Form 2

      // DILG text in upper left
      doc.setFontSize(8);
      doc.text('DILG', 40, 40);
      doc.text('BCPC Form No. 2', 40, 50);

      // Republic of the Philippines and Barangay details
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 40, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Barangay:\t ${barangayName},${municipalName}, ${provinceName}`, doc.internal.pageSize.width / 2, 55, { align: 'center' });
      doc.line((doc.internal.pageSize.width / 2) - 30,57,(doc.internal.pageSize.width / 2)+120,57)
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('BARANGAY COUNCIL FOR THE PROTECTION OF CHILDREN (BCPC)', doc.internal.pageSize.width / 2, 70, { align: 'center' });
      doc.text('SUMMARY PROFILE OF CHILDREN', doc.internal.pageSize.width / 2, 90, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('(Ages 0-17 years old)', doc.internal.pageSize.width / 2, 105, { align: 'center' });

      const currentDate = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const formattedDate = currentDate.toLocaleDateString('en-US', options);
      doc.text(`As of    ${formattedDate}`, doc.internal.pageSize.width / 2, 120, { align: 'center' });


      // Fetch data for BCPC Form 2
      if (apiType) {
        try {
          const location = `${provinceName}_${municipalName}_${barangayName}`
          const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY
            }
          });
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            fetchedData = data[0]; // Assuming it returns an array with one object
          } else {
            console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
            // Default empty fetchedData if no data
          }
        } catch (error) {
          console.error(`Error fetching data for ${formName}:`, error);
          // Default empty fetchedData on error
        }
      }

      const head = [
        [
          { content: 'TOTAL\nNUMBER', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
          { content: 'GENDER', colSpan: 2, styles: { halign: 'center' } },
          { content: 'BIRTH\nREGISTRATION', colSpan: 2, styles: { halign: 'center' } },
          { content: 'IMMUNIZATION STATUS\n(0 to 11 months)\nn = 0', colSpan: 3, styles: { halign: 'center' } },
          { content: 'NUTRITIONAL HEALTH\nSTATUS (Weight for Age)\n(0 to 71 months)', colSpan: 4, styles: { halign: 'center' } },
          { content: 'EDUCATIONAL\nSTATUS\n(3 to 17 years old)\nn = 114', colSpan: 2, styles: { halign: 'center' } },
          { content: 'CHILD LABOR OR\nINCIDENCE\n(5 to 17 years old)\nn = 103', colSpan: 2, styles: { halign: 'center' } },
          { content: 'MEMBERSHIP IN\nORGANIZATIONS\n(5 to 17 years old)\nn = 103', colSpan: 2, styles: { halign: 'center' } },
        ],
        [
          { content: 'Male', styles: { halign: 'center' } },
          { content: 'Female', styles: { halign: 'center' } },
          { content: 'Registered', styles: { halign: 'center' } },
          { content: 'Not\nRegistered', styles: { halign: 'center' } },
          { content: 'Fully\nImmunized\nChild (FIC)', styles: { halign: 'center' } },
          { content: 'Not\nImmunized\nChild (FIC)', styles: { halign: 'center' } },
          { content: 'Incomplete', styles: { halign: 'center' } },
          { content: 'Normal', styles: { halign: 'center' } },
          { content: 'Under-\nweight', styles: { halign: 'center' } },
          { content: 'Severely\nUW', styles: { halign: 'center' } },
          { content: 'Over-\nweight', styles: { halign: 'center' } },
          { content: 'Out-of-school\nchildren (OSC)', styles: { halign: 'center' } },
          { content: 'In-school\nchildren\n(ISC)', styles: { halign: 'center' } },
          { content: 'Engaged', styles: { halign: 'center' } },
          { content: 'Not\nEngaged', styles: { halign: 'center' } },
          { content: 'Member', styles: { halign: 'center' } },
          { content: 'Non-\nmember', styles: { halign: 'center' } },
        ]
      ];

      const body = [
        [
          fetchedData.total_number || '0', // Total Number
          fetchedData.male_count || '0', // Male
          fetchedData.female_count || '0', // Female
          fetchedData.registered_birth || '0', // Registered
          fetchedData.not_registered_birth || '0', // Not Registered
          fetchedData.fully_immunized_child || '0', // Fully Immunized Child (FIC)
          fetchedData.not_immunized_child || '0', // Not Immunized Child (FIC)
          fetchedData.incomplete_immunization || '0', // Incomplete
          fetchedData.normal_nutrition || '0', // Normal
          fetchedData.underweight_nutrition || '0', // Underweight
          fetchedData.severely_uw_nutrition || '0', // Severely UW
          fetchedData.overweight_nutrition || '0', // Overweight
          fetchedData.out_of_school_children || '0', // Out-of-school children (OSC)
          fetchedData.in_school_children || '0', // In-school children (ISC)
          fetchedData.engaged_child_labor || '0', // Engaged
          fetchedData.not_engaged_child_labor || '0', // Not Engaged
          fetchedData.member_org || '0', // Member
          fetchedData.non_member_org || '0', // Non-member
        ]
      ];

      autoTable(doc, {
        startY: 170, // Changed startY to 170
        head: head,
        body: body,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 8,
        },
        columnStyles: {
          0: {halign: 'center'}, // TOTAL NUMBER
          1: {cellWidth: 40, halign: 'center'}, // Male
          2: {cellWidth: 40, halign: 'center'}, // Female
          3: {halign: 'center'}, // Registered
          4: {halign: 'center'}, // Not Registered
          5: {halign: 'center'}, // Fully Immunized Child
          6: {halign: 'center'}, // Not Immunized Child (FIC)
          7: {halign: 'center'}, // Incomplete
          8: {halign: 'center'},  // Normal
          9: {halign: 'center'},  // Underweight
          10: {halign: 'center'}, // Severely UW
          11: {halign: 'center'}, // Overweight
          12: {halign: 'center'}, // Out-of-school children (OSC)
          13: {halign: 'center'}, // In-school children (ISC)
          14: {halign: 'center'}, // Engaged
          15: {halign: 'center'}, // Not Engaged
          16: {halign: 'center'}, // Member
          17: {halign: 'center'}, // Non-member
        },
      });

      // Signatures at the bottom
      doc.setFontSize(10);
      doc.text('Prepared by:', 40, doc.internal.pageSize.height - 100);
      doc.text('Certified by:', doc.internal.pageSize.width - 220, doc.internal.pageSize.height - 100);

      doc.line(40, doc.internal.pageSize.height - 70, 180, doc.internal.pageSize.height - 70);
      doc.text('Barangay Secretary', 110, doc.internal.pageSize.height - 55, { align: 'center' });

      doc.line(220, doc.internal.pageSize.height - 70, 420, doc.internal.pageSize.height - 70);
      doc.text('Kagawad Committee on Women and', 320, doc.internal.pageSize.height - 55, { align: 'center' });
      doc.text('Children\'s Welfare', 320, doc.internal.pageSize.height - 45, { align: 'center' });

      doc.line(doc.internal.pageSize.width - 220, doc.internal.pageSize.height - 70, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 70);
      doc.text('BCPC Chairman', doc.internal.pageSize.width - 130, doc.internal.pageSize.height - 55, { align: 'center' });
      doc.text('Punong Barangay', doc.internal.pageSize.width - 130, doc.internal.pageSize.height - 45, { align: 'center' });


    } else if (formName === 'Registered Barangay Inhabitants') {
      doc = new jsPDF("portrait", "pt", "A4"); // Portrait for Registered Barangay Inhabitants

      // Header text
      doc.setFontSize(10);
      doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 40, { align: 'center' });
      doc.text('Department of Interior and Local Government', doc.internal.pageSize.width / 2, 50, { align: 'center' });
      doc.text('CITY/MUNICIPAL GOVERNMENT OPERATIONS OFFICE', doc.internal.pageSize.width / 2, 60, { align: 'center' });
      doc.text(`${municipalName},${provinceName}`, doc.internal.pageSize.width / 2, 70, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('UPDATED RECORD OF BARANGAY INHABITANTS', doc.internal.pageSize.width / 2, 90, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const currentDate = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const formattedDate = currentDate.toLocaleDateString('en-US', options);
      doc.text(`as of ${formattedDate}`, doc.internal.pageSize.width / 2, 105, { align: 'center' });

      // Fetch data for Registered Barangay Inhabitants
      let rawFetchedData = []; // To store the array from the API
      if (apiType) {
        try {
          const location = `${provinceName}_${municipalName}_${barangayName}`
          const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY
            }
          });
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            rawFetchedData = data; // fetchedData is now the array of purok objects
          } else {
            console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
            rawFetchedData = []; // Ensure it's an empty array if no data
          }
        } catch (error) {
          console.error(`Error fetching data for ${formName}:`, error);
          rawFetchedData = []; // Ensure it's an empty array on error
        }
      }

      // Calculate totals from rawFetchedData
      let totalZones = rawFetchedData.length;
      let totalHouses = 0;
      let totalInhabitants = 0;
      let totalAssemblyMembers = 0;
      let totalChildren = 0;

      rawFetchedData.forEach(item => {
        totalHouses += Number(item.no_house) || 0; // Convert to number
        totalInhabitants += Number(item.no_inhabitants) || 0; // Convert to number
        totalAssemblyMembers += Number(item.no_ass_members) || 0; // Convert to number
        totalChildren += Number(item.no_child) || 0; // Convert to number
      });

      // Barangay details
      const textStart = 80
      const lineStart = 280
      const lineEnd = 450
      doc.setFontSize(10);
      doc.text('NAME OF BARANGAY:', textStart, 130);
      doc.line(lineStart, 130, lineEnd, 130); // Line for Barangay Name
      doc.text(barangayName, lineStart, 127); // Dynamic Barangay Name

      doc.text('Total No. of Zone/Sitio/Purok:', textStart, 145);
      doc.line(lineStart, 145, lineEnd, 145); // Line for Total No. of Zone/Sitio/Purok
      doc.text(totalZones.toString(), lineStart, 142); // Dynamic Total No. of Zone/Sitio/Purok

      doc.text('Total No. of Houses:', textStart, 160);
      doc.line(lineStart, 160, lineEnd, 160); // Line for Total No. of Houses
      doc.text(totalHouses.toString(), lineStart, 157); // Dynamic Total No. of Houses

      doc.text('Total No. of Inhabitants (All):', textStart, 175);
      doc.line(lineStart, 175, lineEnd, 175); // Line for Total No. of Inhabitants (All)
      doc.text(totalInhabitants.toString(), lineStart, 172); // Dynamic Total No. of Inhabitants (All)

      doc.text('Total No. of Brgy Assembly Members:', textStart, 190);
      doc.line(lineStart, 190, lineEnd, 190); // Line for Total No. of Brgy Assembly Members
      doc.text(totalAssemblyMembers.toString(), lineStart, 187); // Dynamic Total No. of Brgy Assembly Members

      doc.text('Total No. of Children (below 15 years old):', textStart, 205);
      doc.line(lineStart, 205, lineEnd, 205); // Line for Total No. of Children (below 15 years old)
      doc.text(totalChildren.toString(), lineStart, 202); // Dynamic Total No. of Children (below 15 years old)

      // Table for Zone/Sitio Purok RBI
      const head = [
        ['Zone/Sitio Purok RBI', 'No. of Houses', 'No. Of Inhabitants', 'No. of Assembly Members (15 and above)', 'No. of Children (below 15)', 'Remarks']
      ];

      const tableBody = rawFetchedData.map(item => [
        item.purok || '',
        item.no_house || '',
        item.no_inhabitants || '',
        item.no_ass_members || '',
        item.no_child || '',
        item.remarks || ''
      ]);

      // Add the total row at the end of the dynamically generated rows
      tableBody.push([
        { content: 'TOTAL', colSpan: 1, styles: { fontStyle: 'bold', halign: 'center' } },
        { content: totalHouses.toString(), styles: { fontStyle: 'bold', halign: 'center' } },
        { content: totalInhabitants.toString(), styles: { fontStyle: 'bold', halign: 'center' } },
        { content: totalAssemblyMembers.toString(), styles: { fontStyle: 'bold', halign: 'center' } },
        { content: totalChildren.toString(), styles: { fontStyle: 'bold', halign: 'center' } },
        { content: '', styles: { fontStyle: 'bold', halign: 'center' } } // Empty for remarks total
      ]);

      autoTable(doc, {
        startY: 220, // Adjusted startY for table
        head: head,
        body: tableBody,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
          halign: 'center', // Default to center for all cells in this table
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: 80 }, // Zone/Sitio Purok RBI
          1: { cellWidth: 70 }, // No. of Houses
          2: { cellWidth: 70 }, // No. Of Inhabitants
          3: { cellWidth: 90 }, // No. of Assembly Members (15 and above)
          4: { cellWidth: 90 }, // No. of Children (below 15)
        },
      });

      // Signatures at the bottom
      doc.setFontSize(10);
      doc.text('Prepared by:', 40, doc.internal.pageSize.height - 100);
      doc.text('Submitted by:', doc.internal.pageSize.width - 200, doc.internal.pageSize.height - 100);

      doc.line(40, doc.internal.pageSize.height - 70, 180, doc.internal.pageSize.height - 70);
      doc.text('Barangay Secretary', 110, doc.internal.pageSize.height - 55, { align: 'center' });

      doc.line(doc.internal.pageSize.width - 200, doc.internal.pageSize.height - 70, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 70);
      doc.text('Punong Barangay', doc.internal.pageSize.width - 120, doc.internal.pageSize.height - 55, { align: 'center' });


    } else if (formName === 'RBI Form A') {
        const printRBIFormAHeader = (doc, reportLocationData, householdNo, psgCodes) => {
          const { regionName, provinceName, municipalName, barangayName } = reportLocationData;
          const { region_code, province_code, municipal_code, barangay_code } = psgCodes;

          // Top Left: RBI FORM A
          doc.setFontSize(8);
          doc.text('RBI FORM A', 40, 40);

          // Main Title
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text('RECORD OF BARANGAY INHABITANTS BY HOUSEHOLD', doc.internal.pageSize.width / 2, 60, { align: 'center' });

          // Barangay Details and Household No.
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text('A. REGION:', 40, 90);
          doc.text(regionName, 160, 90);
          doc.rect(155, 77, 230, 15);

          doc.text('B. PROVINCE:', 40, 105);
          doc.text(provinceName, 160, 105);
          doc.rect(155, 92, 230, 15);

          doc.text('C. CITY/MUNICIPALITY:', 40, 120);
          doc.text(municipalName, 160, 120);
          doc.rect(155, 107, 230, 15);

          doc.text('D. BARANGAY:', 40, 135);
          doc.text(barangayName, 160, 135);
          doc.rect(155, 122, 230, 15);

          doc.text('E. HOUSEHOLD NO.:', 40, 150);
          doc.text(householdNo || 'N/A', 160, 150); // Use the actual householdNo
          doc.rect(155, 137, 160, 15);

          // Right side: PSG codes
          doc.setFontSize(10); // Reset font size
          doc.text(region_code?.toString() || '', 330, 90);
          doc.text(province_code?.toString() || '', 330, 105);
          doc.text(municipal_code?.toString() || '', 330, 120);
          doc.text(barangay_code?.toString() || '', 330, 135);
          doc.setFontSize(7);
          let psgY = 82;
          doc.text('PSG', 393, psgY + 5);
          doc.text('c', 399, psgY + 13);
          doc.text('o', 399, psgY + 23);
          doc.text('d', 399, psgY + 33);
          doc.text('e', 399, psgY + 43);
          doc.text('s', 399, psgY + 53);
          doc.rect(315, psgY - 5, 100, 60);

          // Date Accomplished
          doc.setFontSize(10);
          doc.text('DATE ACCOMPLISHED', doc.internal.pageSize.width - 275, 150);
          doc.rect(doc.internal.pageSize.width - 150, 137, 100, 15);
        };
        // Function to print RBI Form A footer
        const printRBIFormAFooter = (doc, finalY) => {
          doc.setFontSize(10);
          doc.text('Prepared by:', 40, finalY);
          doc.line(40, finalY + 50, 180, finalY + 50); // Line for signature
          doc.text('Name of Household Head/Member', 110, finalY + 60, { align: 'center' });
          doc.text('(Signature over Printed Name)', 110, finalY + 70, { align: 'center' });

          doc.text('Certified Correct:', doc.internal.pageSize.width / 2 - 70, finalY);
          doc.line(doc.internal.pageSize.width / 2 - 70, finalY + 50, doc.internal.pageSize.width / 2 + 70, finalY + 50); // Line for signature
          doc.text('Barangay Secretary', doc.internal.pageSize.width / 2, finalY + 60, { align: 'center' });
          doc.text('(Signature over Printed Name)', doc.internal.pageSize.width / 2, finalY + 70, { align: 'center' });

          doc.text('Validated by:', doc.internal.pageSize.width - 200, finalY);
          doc.line(doc.internal.pageSize.width - 200, finalY + 50, doc.internal.pageSize.width - 40, finalY + 50); // Line for signature
          doc.text('Punong Barangay', doc.internal.pageSize.width - 120, finalY + 60, { align: 'center' });
          doc.text('(Signature over Printed Name)', doc.internal.pageSize.width - 120, finalY + 70, { align: 'center' });
        };
        doc = new jsPDF("landscape", "pt", "A4");

        // Fetch PSG codes first, as they are part of the header
        let rawFetchedDataPSG = [];
        try {
            const location = `${provinceName}_${municipalName}_${barangayName}`
            const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
            const response = await fetch(apiUrl, {
              method: 'GET',
              headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
              }
            });
            const dataAsText = await response.text();
            let data;
            try {
                data = JSON.parse(dataAsText);
            } catch (parseError) {
                console.error("Error parsing JSON from fetched text:", parseError);
                data = []; // Initialize as empty array on parsing error to prevent further errors
            }
            if (Array.isArray(data) && data.length > 0) {
                rawFetchedDataPSG = data;
            } else {
                console.warn(`API for PSG returned an empty array or non-array data. Data received:`, data);
                rawFetchedDataPSG = [];
            }
        } catch (error) {
            console.error(`Error fetching PSG data for ${formName}:`, error);
            rawFetchedDataPSG = [];
        }

        const psgCodes = rawFetchedDataPSG.length > 0 ? rawFetchedDataPSG[0] : {};

        // Fetch data for RBI Form A (individual records)
        let rawFetchedData = [];
        if (apiType) {
            try {
                const location = `${provinceName}_${municipalName}_${barangayName}`
                const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
                const response = await fetch(apiUrl, {
                  method: 'GET',
                  headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY
                  }
                });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    rawFetchedData = data;
                } else {
                    console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                    rawFetchedData = [];
                }
            } catch (error) {
                console.error(`Error fetching data for ${formName}:`, error);
                rawFetchedData = [];
            }
        }

        // Group data by household_no
        const groupedByHousehold = rawFetchedData.reduce((acc, current) => {
            const householdNo = current.household_no;
            if (!acc[householdNo]) {
                acc[householdNo] = [];
            }
            acc[householdNo].push(current);
            return acc;
        }, {});

        // Table Headers
        const head = [
            [
                { content: 'NAME (1)', colSpan: 4, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'ADDRESS (2)', colSpan: 3, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'PLACE OF BIRTH\n(3)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'DATE OF BIRTH\n(4)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'SEX\n(5)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'CIVIL STATUS\n(6)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'CITIZENSHIP\n(7)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'OCCUPATION\n(8)', rowSpan: 2, styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
            ],
            [
                { content: 'LAST NAME\n(1.1)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'FIRST NAME\n(1.2)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'MIDDLE NAME\n(1.3)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'EXT\n(1.4)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'NO.\n(2.1)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'STREET NAME\n(2.2)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
                { content: 'NAME OF\nSUBDIVISION/\nZONE/SITIO/\nPUROK (IF\nAPPLICABLE)\n(2.3)', styles: { halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: [0, 0, 0] } },
            ]
        ];

        let isFirstHousehold = true;
        for (const householdNo of Object.keys(groupedByHousehold)) {
            if (!isFirstHousehold) {
                doc.addPage();
            }
            isFirstHousehold = false;

            // Print header for the current household
            printRBIFormAHeader(doc, reportLocationData, householdNo, psgCodes);

            // Data for the current household
            const householdData = groupedByHousehold[householdNo];
            const body = householdData.map(item => [
                item.last_name || '',
                item.first_name || '',
                item.middle_name || '',
                item.ext || '',
                item.no_address || '',
                item.street_name || '',
                item.subdivision_name || '',
                item.place_of_birth || '',
                item.date_of_birth ? new Date(item.date_of_birth).toLocaleDateString() : '',
                item.sex || '',
                item.civil_status || '',
                item.citizenship || '',
                item.occupation || '',
            ]);

            autoTable(doc, {
                startY: 170, // Start table after the header information
                head: head,
                body: body,
                theme: 'grid',
                styles: {
                    fontSize: 10,
                    cellPadding: 4,
                    overflow: 'linebreak',
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    textColor: [0, 0, 0],
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [0, 0, 0],
                    fontSize: 8,
                },
                columnStyles: {
                    0: {halign: 'left',}, // LAST NAME (1.1)
                    1: {halign: 'left',}, // FIRST NAME (1.2)
                    2: {halign: 'left',}, // MIDDLE NAME (1.3)
                    3: {halign: 'left',},  // EXT (1.4)
                    4: {halign: 'left',},  // NO. (2.1)
                    5: {halign: 'left',},  // STREET NAME (2.2)
                    6: {halign: 'left',},  // NAME OF SUBDIVISION... (2.3)
                    7: {halign: 'left',},  // PLACE OF BIRTH (3)
                    8: {halign: 'left',},  // DATE OF BIRTH (4)
                    9: {halign: 'left',},  // SEX (5)
                    10: {halign: 'center',}, // CIVIL STATUS (6)
                    11: {halign: 'left',}, // CITIZENSHIP (7)
                    12: {halign: 'left',}, // OCCUPATION (8)
                },
                didDrawPage: (data) => {
                    // Redraw header on subsequent pages if the table for a single household spans multiple pages
                    if (data.pageNumber > 1) {
                        printRBIFormAHeader(doc, reportLocationData, householdNo, psgCodes);
                    }
                }
            });

            // Place signatures after the table for the current household doc.internal.pageSize.width
            let finalY = doc.lastAutoTable.finalY + 30;
            const footerHeight = 60; // Approximate height for the footer signatures
            const bottomMargin = 40; // Desired bottom margin

            // Check if footer will fit on the current page. If not, add a new page.
            if (finalY + footerHeight > doc.internal.pageSize.height - bottomMargin) {
                doc.addPage();
                printRBIFormAHeader(doc, reportLocationData, householdNo, psgCodes); // Redraw header on new page
                // Reset finalY to be below the new header, with some buffer
                finalY = 170 + 30; // 170 is the startY for the table, 30 for buffer after potential table area
            }

            printRBIFormAFooter(doc, doc.internal.pageSize.height - 100);
        }
    } else if (formName === 'RBI Form B') {
      doc = new jsPDF("portrait", "pt", "A4"); // Portrait for RBI Form B

      let rawFetchedData = [];
      if (apiType) {
        try {
          const location = `${provinceName}_${municipalName}_${barangayName}`
          const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY
            }
          });
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            rawFetchedData = data;
          } else {
            console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
            rawFetchedData = [];
          }
        } catch (error) {
          console.error(`Error fetching data for ${formName}:`, error);
          rawFetchedData = [];
        }
      }

      rawFetchedData.forEach((entry, index) => {
        if (index > 0) {
          doc.addPage(); // Add a new page for each subsequent entry
        }

        // RBI Form B header
        doc.setFontSize(8);
        doc.text('RBI Form B', 40, 40);

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('INDIVIDUAL RECORD OF BARANGAY INHABITANT', doc.internal.pageSize.width / 2, 60, { align: 'center' });

        // Region, City/Mun, Province, Barangay
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('REGION', 40, 80);
        doc.line(95, 82, 250, 82);
        doc.text(regionName, 95 + 30, 79);

        doc.text('CITY/MUN', 280, 80);
        doc.line(340, 82, 500, 82);
        doc.text(municipalName, 340 + 30, 79);

        doc.text('PROVINCE', 40, 95);
        doc.line(95, 97, 250, 97);
        doc.text(provinceName, 95 + 30, 94);

        doc.text('BARANGAY', 280, 95);
        doc.line(340, 97, 500, 97);
        doc.text(barangayName, 340 + 30, 94);

        // LINES (X,Y,X2,Y2) - Outer border
        doc.line(30,110,30,doc.internal.pageSize.height - 60)
        doc.line(doc.internal.pageSize.width - 30,110,doc.internal.pageSize.width - 30,doc.internal.pageSize.height - 60)
        doc.line(30,110,doc.internal.pageSize.width - 30,110)
        doc.line(30,doc.internal.pageSize.height - 60,doc.internal.pageSize.width - 30,doc.internal.pageSize.height - 60)

        // I. PERSONAL INFORMATION
        const startY_personalInfo = 130; // Starting Y for this section
        const lineSpacing_personalInfo = 25; // Vertical space between lines of info

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('I. PERSONAL INFORMATION', 40, startY_personalInfo);

        doc.setFont(undefined, 'normal');
        let currentY = startY_personalInfo + lineSpacing_personalInfo; // Start Y for NAME

        doc.text('NAME:', 40, currentY);
        const fullName = `${entry.last_name || ''} \t\t\t ${entry.first_name || ''}  \t\t\t ${entry.middle_name || ''}  \t\t\t\t ${entry.suffix || ''}`.trim();
        doc.text(`\t ${fullName}`, 80, currentY); // Fill in the full name
        doc.line(80, currentY + 2, 550, currentY + 2); // Line for Name
        doc.text('(Last)', 130, currentY + 15, { align: 'center' });
        doc.text('(First)', 260, currentY + 15, { align: 'center' });
        doc.text('(Middle)', 390, currentY + 15, { align: 'center' });
        doc.text('(Ext)', 485, currentY + 15, { align: 'center' });

        currentY += lineSpacing_personalInfo * 2; // Move down for Date of Birth

        // Date of Birth and Place of Birth
        doc.text('DATE OF BIRTH:', 40, currentY);
        const birthdate = entry.birthdate ? new Date(entry.birthdate).toLocaleDateString('en-US') : '';
        doc.text(birthdate, 120, currentY);
        doc.line(120, currentY + 2, 250, currentY + 2);
        doc.text('(mm/dd/yy)', 185, currentY + 15, { align: 'center' });

        doc.text('PLACE OF BIRTH:', 280, currentY);
        doc.text(entry.birthplace || '', 370, currentY);
        doc.line(370, currentY + 2, 550, currentY + 2);

        currentY += lineSpacing_personalInfo * 2; // Move down for Sex/Civil Status

        // Gender, Civil Status
        doc.text('SEX:', 40, currentY);
        doc.rect(80, currentY - 7.5, 10, 10); // Male checkbox
        doc.text('MALE', 95, currentY);
        doc.rect(80, currentY + 15 - 7.5, 10, 10); // Female checkbox
        doc.text('FEMALE', 95, currentY + 15);
        if (entry.sex === 'Male') {
          doc.text('X', 82, currentY + 2); // Mark Male
        } else if (entry.sex === 'Female') {
          doc.text('X', 82, currentY + 17); // Mark Female
        }

        doc.text('CIVIL STATUS:', 225, currentY);
        doc.rect(300, currentY - 7.5, 10, 10); // Single checkbox
        doc.text('SINGLE', 315, currentY);
        doc.rect(300, currentY + 15 - 7.5, 10, 10); // Married checkbox
        doc.text('MARRIED', 315, currentY + 15);
        doc.rect(420, currentY - 7.5, 10, 10); // Widow/er checkbox
        doc.text('WIDOW/ER', 435, currentY);
        doc.rect(420, currentY + 15 - 7.5, 10, 10); // Separated checkbox
        doc.text('SEPARATED', 435, currentY + 15);

        // Mapping civil status to 'X' mark
        if (entry.civil_status === 'Single') {
          doc.text('X', 302, currentY + 2);
        } else if (entry.civil_status === 'Married') {
          doc.text('X', 302, currentY + 17);
        } else if (entry.civil_status === 'Widowed') {
          doc.text('X', 422, currentY + 2);
        } else if (entry.civil_status === 'Separated') {
          doc.text('X', 422, currentY + 17);
        }


        currentY += lineSpacing_personalInfo * 2; // Move down for Citizenship

        // Citizenship
        doc.text('CITIZENSHIP:', 40, currentY);
        doc.text(entry.citizenship || '', 120, currentY);
        doc.line(120, currentY + 2, 400, currentY + 2);

        currentY += lineSpacing_personalInfo; // Move down for Profession

        // Profession/Occupation
        doc.text('PROFESSION/OCCUPATION:', 40, currentY);
        doc.text(entry.profession || '', 180, currentY);
        doc.line(180, currentY + 2, 550, currentY + 2);

        currentY += lineSpacing_personalInfo; // Move down for Residence Address

        // Residence Address
        doc.text('RESIDENCE ADDRESS:', 40, currentY);
        const houseStreet = `${entry.house_no || ''} \t\t\t ${entry.street || ''}`.trim();
        doc.text('\t'+houseStreet, 160, currentY);
        doc.line(160, currentY + 2, 550, currentY + 2);
        doc.text('House No.', 270, currentY + 15, { align: 'center' });
        doc.text('Street Name', 400, currentY + 15, { align: 'center' });

        currentY += lineSpacing_personalInfo; // Move down for second line of Residence Address
        doc.text(entry.purok_sitio || '', 160, currentY);
        doc.line(160, currentY + 2, 550, currentY + 2);
        doc.text('Subdivision Name/Zone/Sitio/Purok', 330, currentY + 15, { align: 'center' });

        // Certification
        currentY += lineSpacing_personalInfo*3;
        //let certY = doc.internal.pageSize.height - 250; // Position Certification text from bottom
        doc.text('I hereby certify that the above information are true and correct to the best of my knowledge.', doc.internal.pageSize.width / 2, currentY ,{ align: 'center' });

        // Date Accomplished and Name/Signature
        currentY += lineSpacing_personalInfo*3;
        //let sigY = doc.internal.pageSize.height - 180; // Position signature lines
        doc.line(40, currentY, 250, currentY);
        doc.text('Date Accomplished', 145, currentY + 15, { align: 'center' });

        doc.line(280, currentY, 550, currentY);
        doc.text('Name/Signature of Person Accomplishing the form', 415, currentY + 15, { align: 'center' });

        // Attested by
        currentY += lineSpacing_personalInfo*4;
        //let attestedY = doc.internal.pageSize.height - 110; // Position Attested by
        doc.text('Attested by:', 40, currentY);
        doc.line(100, currentY, 250, currentY);
        doc.text('Barangay Secretary', 170, currentY + 15, { align: 'center' });

        // Thumbmarks
        //let thumbY = doc.internal.pageSize.height - 150; // Position Thumbmarks boxes
        doc.rect(320, currentY-60, 60, 60); // Left Thumbmark box
        doc.text('Left', 350, currentY + 15, { align: 'center' }); // Adjusted text position
        doc.text('Thumbmark', 350, currentY + 25, { align: 'center' }); // Adjusted text position

        doc.rect(420, currentY-60, 60, 60); // Right Thumbmark box
        doc.text('Right', 450, currentY + 15, { align: 'center' }); // Adjusted text position
        doc.text('Thumbmark', 450, currentY + 25, { align: 'center' }); // Adjusted text position

        // Household Number
        currentY += lineSpacing_personalInfo*4;
        //let householdNoY = doc.internal.pageSize.height - 70; // Position Household Number
        doc.text(entry.household_no || '', 420, currentY);
        doc.line(350, currentY + 2, 500, currentY + 2);
        doc.text('Household Number', 425, currentY + 15, { align: 'center' });

        // Note
        doc.setFontSize(8);
        doc.text('Note: The Household No. shall be filled up by the Barangay Secretary', 40, doc.internal.pageSize.height - 70);
      });


    } else if (formName === 'Barangay Governance Management System') {
        doc = new jsPDF("portrait", "pt", "A4");
        // Fetch data for Barangay Governance Management System
        let rawFetchedData = [];
        if (apiType) {
            try {
                  const location = `${provinceName}_${municipalName}_${barangayName}`
                  const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
                  const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                      'x-api-key': process.env.REACT_APP_API_KEY
                    }
                  });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    rawFetchedData = data;
                } else {
                    console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                    rawFetchedData = [];
                }
            } catch (error) {
                console.error(`Error fetching data for ${formName}:`, error);
                rawFetchedData = [];
            }
        }
        // Outer border
        doc.rect(30, 100, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 150);

        // Header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Barangay Governance Performance Management System', doc.internal.pageSize.width / 2, 50, { align: 'center' });
        doc.text('DATA CAPTURE FORM', doc.internal.pageSize.width / 2, 65, { align: 'center' });
        doc.text('CY \n Year Under Review', doc.internal.pageSize.width / 2, 80, { align: 'center' }); // Year placeholder

        // A. GENERAL INFORMATION
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('A. GENERAL INFORMATION', 40, 120);
        doc.line(40, 122, 180, 122); // Underline

        doc.setFont(undefined, 'normal');
        let currentY = 135;
        const labelX = 50;
        const lineX = 200;
        const lineLength = 200;
        const lineHeight = 13;

        // Region
        doc.setFontSize(8);
        doc.text('Region', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY + 2);
        doc.text(reportLocationData.regionName || '', lineX + 5, currentY);
        currentY += lineHeight;

        // Province
        doc.text('Province', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        doc.text(reportLocationData.provinceName || '', lineX + 5, currentY);
        currentY += lineHeight;

        // Congressional District
        doc.text('Congressional District', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        // doc.text(fetchedData.congressional_district || '', lineX + 5, currentY); // Assuming data field
        currentY += lineHeight;

        // City/Municipality
        doc.text('City/Municipality', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        doc.text(reportLocationData.municipalName || '', lineX + 5, currentY);
        currentY += lineHeight;

        // Barangay
        doc.text('Barangay', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        doc.text(reportLocationData.barangayName || '', lineX + 5, currentY);
        currentY += lineHeight;

        // Legal Basis for Creation
        doc.text('Legal Basis for Creation', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        // doc.text(fetchedData.legal_basis || '', lineX + 5, currentY); // Assuming data field
        currentY += lineHeight;

        // Land Area
        doc.text('Land Area', labelX, currentY);
        doc.line(lineX, currentY + 2, lineX + lineLength, currentY);
        // doc.text(fetchedData.land_area || '', lineX + 5, currentY); // Assuming data field
        currentY += lineHeight + 5;

        // Barangay Category (checkboxes)
        doc.text('Barangay Category (please check appropriate box or boxes)', labelX, currentY);
        currentY += lineHeight;

        const checkboxY = currentY - 5;
        const checkboxGap = 80;
        let checkboxX = labelX + 100;

        doc.rect(checkboxX, checkboxY, 10, 10); doc.text('Urban', checkboxX + 15, currentY + 4); checkboxX += checkboxGap;
        doc.rect(checkboxX, checkboxY, 10, 10); doc.text('Agricultural', checkboxX + 15, currentY + 4); checkboxX += checkboxGap;
        doc.rect(checkboxX, checkboxY, 10, 10); doc.text('Upland', checkboxX + 15, currentY + 4); checkboxX += checkboxGap;
        doc.rect(checkboxX, checkboxY, 10, 10); doc.text('Landlocked', checkboxX + 15, currentY + 4);

        checkboxX = labelX + 100;

        doc.rect(checkboxX, checkboxY + lineHeight + 5, 10, 10); doc.text('Rural', checkboxX + 15, currentY + 9 + lineHeight); checkboxX += checkboxGap;
        doc.rect(checkboxX, checkboxY + lineHeight + 5, 10, 10); doc.text('Coastal', checkboxX + 15, currentY + 9 + lineHeight); checkboxX += checkboxGap;
        doc.rect(checkboxX, checkboxY + lineHeight + 5, 10, 10); doc.text('Lowland', checkboxX + 15, currentY + 9 + lineHeight);
        currentY += lineHeight + 5;

        currentY += lineHeight + 5;

        // Population data
        doc.text('Total No. of population', labelX, currentY);
        doc.setFontSize(8);
        doc.text('(Use NSO Data. In case no NSO data, the RBI shall be used)', labelX + 120, currentY);
        doc.setFontSize(8);
        currentY += lineHeight + 5;

        doc.text('Total', labelX + 10, currentY);
        doc.line(labelX + 50, currentY + 2, labelX + 100, currentY + 2);
        doc.text(rawFetchedData[0]?.population.toString() || '', labelX + 65, currentY); // Placeholder
        doc.text('Male', labelX + 120, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        doc.text(rawFetchedData[0]?.male.toString() || '', labelX + 165, currentY); // Placeholder
        doc.text('Female', labelX + 220, currentY);
        doc.line(labelX + 260, currentY + 2, labelX + 310, currentY + 2);
        doc.text(rawFetchedData[0]?.female.toString() || '', labelX + 275, currentY); // Placeholder
        currentY += lineHeight + 5;

        doc.text('Total No. of Households', labelX, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        doc.text(rawFetchedData[0]?.no_hh.toString() || '', labelX + 165, currentY); // Placeholder
        currentY += lineHeight;

        doc.text('Total No. of Families', labelX, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        doc.text(rawFetchedData[0]?.no_families.toString() || '', labelX + 165, currentY); // Placeholder
        currentY += lineHeight;

        doc.text('Total No. of Labor Force', labelX, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        doc.text(rawFetchedData[0]?.labor_force.toString() || '', labelX + 165, currentY); // Placeholder
        currentY += lineHeight;

        doc.text('Total No. of Registered Voters', labelX, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        currentY += lineHeight;

        doc.text('Total No. of Precincts', labelX, currentY);
        doc.line(labelX + 150, currentY + 2, labelX + 200, currentY + 2);
        currentY += lineHeight + 5;

        // Total Revenues
        doc.text('Total Revenues', labelX, currentY);
        currentY += lineHeight;

        doc.text('Local Source', labelX + 100, currentY);
        doc.line(labelX + 220, currentY + 2, labelX + 370, currentY + 2);
        doc.text('P', labelX + 210, currentY);
        currentY += lineHeight;

        doc.text('IRA', labelX + 100, currentY);
        doc.line(labelX + 220, currentY + 2, labelX + 370, currentY + 2);
        doc.text('P', labelX + 210, currentY);
        currentY += lineHeight;

        doc.text('Others (Pls. Specify)', labelX + 100, currentY);
        doc.line(labelX + 220, currentY + 2, labelX + 370, currentY + 2);
        doc.line(labelX + 220, currentY + 5, labelX + 370, currentY + 5);
        doc.text('P', labelX + 210, currentY);
        currentY += lineHeight + 5 + 5;

        // Total Expenditures
        doc.text('Total Expenditures', labelX, currentY);
        doc.line(labelX + 220, currentY + 2, labelX + 370, currentY + 2);
        // doc.text(fetchedData.total_expenditures || '', labelX + 165, currentY); // Assuming data field
        doc.text('P', labelX + 210, currentY);
        currentY += lineHeight + 5;

        // Basic Utilities/Services
        doc.text('Basic Utilities/Services:', labelX, currentY);
        currentY += lineHeight;

        doc.text('Power Supply', labelX + 30, currentY);
        checkboxX = labelX + 150;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('MERALCO', checkboxX + 15, currentY); checkboxX += 80;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('Electric Cooperative', checkboxX + 15, currentY); checkboxX += 120;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('Others', checkboxX + 15, currentY);  doc.line(checkboxX + 50, currentY, checkboxX + 100, currentY);
        currentY += lineHeight;

        doc.text('Water Supply', labelX + 30, currentY);
        checkboxX = labelX + 150;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('First Level', checkboxX + 15, currentY); checkboxX += 80;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('Second Level', checkboxX + 15, currentY); checkboxX += 120;
        doc.rect(checkboxX, currentY - 7.5, 10, 10); doc.text('Third Level', checkboxX + 15, currentY);
        currentY += lineHeight + 5;

        doc.text('Transportation (Please Specify)', labelX + 30, currentY);
        doc.line(doc.internal.pageSize.width - 230, currentY + 2, doc.internal.pageSize.width - 80, currentY + 2);
        // doc.text(fetchedData.transportation || '', labelX + 185, currentY); // Assuming data field
        currentY += lineHeight;

        doc.text('Communication telephone, mobile etc. (name of provider)', labelX + 30, currentY);

        doc.line(doc.internal.pageSize.width - 230, currentY, doc.internal.pageSize.width - 80, currentY);
        // doc.text(fetchedData.communication || '', labelX + 285, currentY); // Assuming data field
        currentY += lineHeight+5;
        doc.line(30, currentY - 15, doc.internal.pageSize.width - 30, currentY - 15); // Underline
        // B. Barangay Human Development Index
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('B. Barangay Human Development Index', 40, currentY);
        doc.setFontSize(8);
        doc.text('(Source: BMIS-BIDANI)', 230, currentY);
        
        

        // Table for Human Development Index
        const child_dead_prop = (rawFetchedData[0]?.died && rawFetchedData[0].died !== 0) ? ((rawFetchedData[0].child_dead / rawFetchedData[0].died) * 100) : 0;
        const female_maternal_death_prop = (rawFetchedData[0]?.died && rawFetchedData[0].died !== 0) ? ((rawFetchedData[0].female_maternal_death / rawFetchedData[0].died) * 100) : 0;
        const malnourished_child_prop = (rawFetchedData[0]?.['0_5'] && rawFetchedData[0]['0_5'] !== 0) ? ((rawFetchedData[0].malnourished_child / rawFetchedData[0]['0_5']) * 100) : 0;
        const makeshift_housing_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].makeshift_housing / rawFetchedData[0].no_hh) * 100 : 0;
        const squatter_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].squatter / rawFetchedData[0].no_hh) * 100 : 0;
        const wo_safe_water_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].wo_safe_water / rawFetchedData[0].no_hh) * 100 : 0;
        const wo_sanitary_toilet_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].wo_sanitary_toilet / rawFetchedData[0].no_hh) * 100 : 0;
        const na_elem_prop = (rawFetchedData[0]?.['6_12'] && rawFetchedData[0]['6_12'] !== 0) ? (rawFetchedData[0].na_elem / rawFetchedData[0]['6_12']) * 100 : 0;
        const na_hs_prop = (rawFetchedData[0]?.['13_16'] && rawFetchedData[0]['13_16'] !== 0) ? (rawFetchedData[0].na_hs / rawFetchedData[0]['13_16']) * 100 : 0;
        const below_poverty_th_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].below_poverty_th / rawFetchedData[0].no_hh) * 100 : 0;
        const below_food_th_prop = (rawFetchedData[0]?.no_hh && rawFetchedData[0].no_hh !== 0) ? (rawFetchedData[0].below_food_th / rawFetchedData[0].no_hh) * 100 : 0;
        const unemployed_prop = (rawFetchedData[0]?.labor_force && rawFetchedData[0].labor_force !== 0) ? (rawFetchedData[0].unemployed / rawFetchedData[0].labor_force) * 100 : 0;

        const hdiHead = [['', 'Magnitude', 'Proportion']];
        const hdiBody = [
            ['1. Proportion of children aged 0-5 years old who died.', rawFetchedData[0]?.child_dead || '0', child_dead_prop.toFixed(2) + '%'],
            ['2. Proportion of women who died due to pregnancy related causes.', rawFetchedData[0]?.female_maternal_death || '0', female_maternal_death_prop.toFixed(2) + '%'],
            ['3. Proportion of children aged 0-5 years old who are malnourished (WA).', rawFetchedData[0]?.malnourished_child || '0', malnourished_child_prop.toFixed(2) + '%'],
            ['4. Proportion of households living in makeshift housing (dwelling).', rawFetchedData[0]?.makeshift_housing || '0', makeshift_housing_prop.toFixed(2) + '%'],
            ['5. Proportion of households that are informal settlers.', rawFetchedData[0]?.squatter || '0', squatter_prop.toFixed(2) + '%'],
            ['6. Proportion of households without access to safe water supply.', rawFetchedData[0]?.wo_safe_water || '0', wo_safe_water_prop.toFixed(2) + '%'],
            ['7. Proportion of households without access to sanitary toilet.', rawFetchedData[0]?.wo_sanitary_toilet || '0', wo_sanitary_toilet_prop.toFixed(2) + '%'],
            ['8. Proportion of children aged 6-12 years old who are not attending elementary school.', rawFetchedData[0]?.na_elem || '0',na_elem_prop.toFixed(2) + '%'],
            ['9. Proportion of children aged 13-16 years old who are not attending secondary school.', rawFetchedData[0]?.na_hs || '0', na_hs_prop.toFixed(2) + '%'],
            ['10. Proportion of households with income below poverty threshold.', rawFetchedData[0]?.below_poverty_th || '0', below_poverty_th_prop.toFixed(2) + '%'],
            ['11. Proportion of households with income below the food (subsistence) threshold.', rawFetchedData[0]?.below_food_th || '0', below_food_th_prop.toFixed(2) + '%'],
            ['12. Proportion of households that experienced food shortage.', '',''],
            ['13. Proportion of persons who are unemployed (15 to 65 years old).', rawFetchedData[0]?.unemployed || '0', unemployed_prop.toFixed(2) + '%'],
            ['14. Proportion of persons who were victims of crimes.', '', ''],
        ];

        autoTable(doc, {
            startY: currentY+10,
            head: hdiHead,
            body: hdiBody,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                lineWidth: 0.5,
                lineColor: [255, 255, 255],
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.5,
                lineColor: [255, 255, 255],
                fontSize: 8,
            },
            columnStyles: {
                0: { cellWidth: 350, halign: 'left' }, // Indicator description
                1: { cellWidth: 60, halign: 'center' }, // Magnitude
                2: { cellWidth: 60, halign: 'center' }, // Proportion
            },
            margin: { left: 60, right: 40 },
        });
    } else if (formName === 'Form No. CMP-03-A') {
        doc = new jsPDF("landscape", "pt", "A4"); // Use landscape for wider table

        // Top Left Header
        doc.setFontSize(8);
        doc.text('Form No. CMP-03-A (Barangay Level / Station Level)', 40, 40);

        // Main Titles
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('ENHANCING OPERATIONAL EFFECTIVENESS', doc.internal.pageSize.width / 2, 60, { align: 'center' });
        doc.text('A. HOUSEHOLD PER BARANGAY', doc.internal.pageSize.width / 2, 75, { align: 'center' });

        // Fetch data for Form No. CMP-03-A
        let rawFetchedData = [];
        if (apiType) {
            try {
                const location = `${provinceName}_${municipalName}_${barangayName}`
                const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
                const response = await fetch(apiUrl, {
                  method: 'GET',
                  headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY
                  }
                });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    rawFetchedData = data;
                } else {
                    console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                    rawFetchedData = [];
                }
            } catch (error) {
                console.error(`Error fetching data for ${formName}:`, error);
                rawFetchedData = [];
            }
        }

        // Table Headers
        const head = [
            [
                'Household Number',
                'Complete address',
                'Name of Family Member',
                'Position in the Family',
                'Age',
                'Civil Status',
                'Occupation/Source of Income'
            ]
        ];

        // Table Body - Map fetchedData to the table rows
        const body = rawFetchedData.map(item => [
            item.household_no || '',
            item.complete_address || '',
            item.fullname || '',
            item.position || '',
            item.age || '',
            item.civil_status || '',
            item.occupation || ''
        ]);

        autoTable(doc, {
            startY: 100, // Start table after the main titles
            head: head,
            body: body,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 6,
                overflow: 'linebreak',
                lineWidth: 0.5,
                lineColor: [255, 255, 255],
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [255, 255, 255], // White background for header
                textColor: [0, 0, 0], // Black text for header
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.5,
                lineColor: [255, 255, 255],
                fontSize: 8,
            },
            columnStyles: {
                0: { cellWidth: 60, halign: 'center' }, // Household Number
                1: {halign: 'left' },  // Complete address
                2: {halign: 'left' },  // Name of Family Member
                3: {halign: 'left' },   // Position in the Family
                4: { cellWidth: 30, halign: 'center' }, // Age
                5: {halign: 'left' },   // Civil Status
                6: {cellWidth: 150, halign: 'left' },  // Occupation
            },
            margin: { left: 40, right: 40 }, // Match image margins
        });
        const totalPages = doc.internal.getNumberOfPages();
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

        // Loop through all pages to add footer and outer border
        for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);

                // Footer: Date on left, Page X of Y on right
                doc.setFontSize(8);
                doc.text(formattedDate, 40, doc.internal.pageSize.height - 30);
                doc.text(`Page ${i} of ${totalPages}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 30, { align: 'right' });
        }
    } else if (formName === 'Literacy Report Form') {
        doc = new jsPDF("landscape", "pt", "A4"); // Changed to landscape

        // Header
        doc.setFontSize(10);
        
        doc.text(`Province of ${provinceName}`, doc.internal.pageSize.width / 2, 55, { align: 'center' });
        doc.text(`Municipality of ${municipalName}`, doc.internal.pageSize.width / 2, 70, { align: 'center' });
        doc.text(`BARANGAY ${barangayName}`, doc.internal.pageSize.width / 2, 85, { align: 'center' });

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('LITERACY RATE FORM (1)', doc.internal.pageSize.width / 2, 105, { align: 'center' });
        doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 40, { align: 'center' });

        // Fetch data for Literacy Report Form
        let rawFetchedData = [];
        if (apiType) {
            try {
                const location = `${provinceName}_${municipalName}_${barangayName}`
                const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
                const response = await fetch(apiUrl, {
                  method: 'GET',
                  headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY
                  }
                });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    rawFetchedData = data;
                } else {
                    console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                    rawFetchedData = [];
                }
            } catch (error) {
                console.error(`Error fetching data for ${formName}:`, error);
                rawFetchedData = [];
            }
        }

        // Table Headers
        const head = [
            [
                { content: 'Purok', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
                { content: '5-12 Y/O', colSpan: 2, styles: { halign: 'center' } },
                { content: '13-18 Y/O', colSpan: 2, styles: { halign: 'center' } },
                { content: '19-23 Y/O', colSpan: 2, styles: { halign: 'center' } },
                { content: '24 and above', colSpan: 2, styles: { halign: 'center' } },
                { content: 'Total', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            ],
            [
                { content: 'Male', styles: { halign: 'center' } },
                { content: 'Female', styles: { halign: 'center' } },
                { content: 'Male', styles: { halign: 'center' } },
                { content: 'Female', styles: { halign: 'center' } },
                { content: 'Male', styles: { halign: 'center' } },
                { content: 'Female', styles: { halign: 'center' } },
                { content: 'Male', styles: { halign: 'center' } },
                { content: 'Female', styles: { halign: 'center' } },
            ]
        ];

        // Table Body - Map fetchedData to the table rows using the specified properties
        const body = rawFetchedData.map(item => [
            item.purok || '',
            item.m1 || '', // 5-12 Male
            item.f1 || '', // 5-12 Female
            item.m2 || '', // 13-18 Male
            item.f2 || '', // 13-18 Female
            item.m3 || '', // 19-23 Male
            item.f3 || '', // 19-23 Female
            item.m4 || '', // 24+ Male
            item.f4 || '', // 24+ Female
            item.total || '',
        ]);

        // Calculate totals for the "TOTAL Population by age group" row based on m1, f1, etc.
        const totalPopulationByAgeGroup = {
            m1: 0, f1: 0,
            m2: 0, f2: 0,
            m3: 0, f3: 0,
            m4: 0, f4: 0,
            total: 0
        };

        rawFetchedData.forEach(item => {
            totalPopulationByAgeGroup.m1 += parseInt(item.m1 || 0);
            totalPopulationByAgeGroup.f1 += parseInt(item.f1 || 0);
            totalPopulationByAgeGroup.m2 += parseInt(item.m2 || 0);
            totalPopulationByAgeGroup.f2 += parseInt(item.f2 || 0);
            totalPopulationByAgeGroup.m3 += parseInt(item.m3 || 0);
            totalPopulationByAgeGroup.f3 += parseInt(item.f3 || 0);
            totalPopulationByAgeGroup.m4 += parseInt(item.m4 || 0);
            totalPopulationByAgeGroup.f4 += parseInt(item.f4 || 0);
            totalPopulationByAgeGroup.total += parseInt(item.total || 0);
        });

        // Calculate totals for the "TOTAL population attended school" row
        // Uses the new properties: asm1-asm4 (male attended school), asf1-asf4 (female attended school)
        const totalAttendedSchool = {
            asm1: 0, asf1: 0,
            asm2: 0, asf2: 0,
            asm3: 0, asf3: 0,
            asm4: 0, asf4: 0,
            total: 0
        };

        rawFetchedData.forEach(item => {
            totalAttendedSchool.asm1 += parseInt(item.asm1 || 0);
            totalAttendedSchool.asf1 += parseInt(item.asf1 || 0);
            totalAttendedSchool.asm2 += parseInt(item.asm2 || 0);
            totalAttendedSchool.asf2 += parseInt(item.asf2 || 0);
            totalAttendedSchool.asm3 += parseInt(item.asm3 || 0);
            totalAttendedSchool.asf3 += parseInt(item.asf3 || 0);
            totalAttendedSchool.asm4 += parseInt(item.asm4 || 0);
            totalAttendedSchool.asf4 += parseInt(item.asf4 || 0);
            // Summing individual attended counts for totalAttendedSchool.total
            totalAttendedSchool.total += (parseInt(item.asm1 || 0) + parseInt(item.asf1 || 0) +
                                      parseInt(item.asm2 || 0) + parseInt(item.asf2 || 0) +
                                      parseInt(item.asm3 || 0) + parseInt(item.asf3 || 0) +
                                      parseInt(item.asm4 || 0) + parseInt(item.asf4 || 0));
        });

        // Calculate Literacy Rates using calculated totals
        const literacyRates = {
            m1: (totalPopulationByAgeGroup.m1 > 0) ? `${Math.round((totalAttendedSchool.asm1 / totalPopulationByAgeGroup.m1) * 100)}%` : '0%',
            f1: (totalPopulationByAgeGroup.f1 > 0) ? `${Math.round((totalAttendedSchool.asf1 / totalPopulationByAgeGroup.f1) * 100)}%` : '0%',
            m2: (totalPopulationByAgeGroup.m2 > 0) ? `${Math.round((totalAttendedSchool.asm2 / totalPopulationByAgeGroup.m2) * 100)}%` : '0%',
            f2: (totalPopulationByAgeGroup.f2 > 0) ? `${Math.round((totalAttendedSchool.asf2 / totalPopulationByAgeGroup.f2) * 100)}%` : '0%',
            m3: (totalPopulationByAgeGroup.m3 > 0) ? `${Math.round((totalAttendedSchool.asm3 / totalPopulationByAgeGroup.m3) * 100)}%` : '0%',
            f3: (totalPopulationByAgeGroup.f3 > 0) ? `${Math.round((totalAttendedSchool.asf3 / totalPopulationByAgeGroup.f3) * 100)}%` : '0%',
            m4: (totalPopulationByAgeGroup.m4 > 0) ? `${Math.round((totalAttendedSchool.asm4 / totalPopulationByAgeGroup.m4) * 100)}%` : '0%',
            f4: (totalPopulationByAgeGroup.f4 > 0) ? `${Math.round((totalAttendedSchool.asf4 / totalPopulationByAgeGroup.f4) * 100)}%` : '0%',
            total: (totalPopulationByAgeGroup.total > 0) ? `${Math.round((totalAttendedSchool.total / totalPopulationByAgeGroup.total) * 100)}%` : '0%',
        };


        const finalRows = [
            ...body,
            // TOTAL population attended school row
            [{ content: 'TOTAL population attended school', styles: { fontStyle: 'bold' } },
            totalAttendedSchool.asm1, totalAttendedSchool.asf1,
            totalAttendedSchool.asm2, totalAttendedSchool.asf2,
            totalAttendedSchool.asm3, totalAttendedSchool.asf3,
            totalAttendedSchool.asm4, totalAttendedSchool.asf4,
            { content: totalAttendedSchool.total.toString(), styles: { fontStyle: 'bold' } }
            ],
            // TOTAL Population by age group row (calculated from fetched m1, f1, etc.)
            [{ content: 'TOTAL Population by age group', styles: { fontStyle: 'bold' } },
            totalPopulationByAgeGroup.m1, totalPopulationByAgeGroup.f1,
            totalPopulationByAgeGroup.m2, totalPopulationByAgeGroup.f2,
            totalPopulationByAgeGroup.m3, totalPopulationByAgeGroup.f3,
            totalPopulationByAgeGroup.m4, totalPopulationByAgeGroup.f4,
            { content: totalPopulationByAgeGroup.total.toString(), styles: { fontStyle: 'bold' } }
            ],
            // Literacy rate row
            [{ content: 'Literacy rate', styles: { fontStyle: 'bold' } },
            literacyRates.m1, literacyRates.f1,
            literacyRates.m2, literacyRates.f2,
            literacyRates.m3, literacyRates.f3,
            literacyRates.m4, literacyRates.f4,
            { content: literacyRates.total, styles: { fontStyle: 'bold' } }
            ]
        ];

        autoTable(doc, {
            startY: 120, // Start table after the main titles
            head: head,
            body: finalRows,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [255, 255, 255], // White background for header
                textColor: [0, 0, 0], // Black text for header
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fontSize: 8,
            },
            columnStyles: {
                0: {cellWidth: 90, halign: 'center' }, // Purok
                1: {halign: 'center' }, // 5-12 Male (m1)
                2: {halign: 'center' }, // 5-12 Female (f1)
                3: {halign: 'center' }, // 13-18 Male (m2)
                4: {halign: 'center' }, // 13-18 Female (f2)
                5: {halign: 'center' }, // 19-23 Male (m3)
                6: {halign: 'center' }, // 19-23 Female (f3)
                7: {halign: 'center' }, // 24+ Male (m4)
                8: {halign: 'center' }, // 24+ Female (f4)
                9: {halign: 'center' }, // Total
            },
            margin: { top: 10, right: 40, bottom: 80, left: 40 }, // Adjust bottom margin for footer
        });

        // Footer
        let finalY = doc.internal.pageSize.height - 120; // Adjust position for footer

        doc.setFontSize(10);
        doc.text('Prepared by:', 40, finalY - 20);
        doc.text('Noted by:', doc.internal.pageSize.width / 2 + 50, finalY - 20);

        doc.line(40, finalY + 20, 200, finalY + 20); // Line for Prepared by
        doc.text('Barangay Secretary', 120, finalY + 30, { align: 'center' });

        doc.line(doc.internal.pageSize.width / 2 + 50, finalY + 20, doc.internal.pageSize.width - 40, finalY + 20); // Line for Noted by
        doc.text('Punong Barangay', doc.internal.pageSize.width / 2 + 140, finalY + 30, { align: 'center' });

        doc.setFontSize(8);
        doc.text('1 The Literacy rate was computed based on the attendance to school by age group (Source: BMIS 2016 and 2017)', 40, doc.internal.pageSize.height - 30);

    } else if (formName === 'Information on education by age and sex') {
        doc = new jsPDF("landscape", "pt", "A4");

        // Function to draw the common header on each page
        const drawCommonHeader = (doc, reportLocationData) => {
            doc.setFontSize(10);
            const currentDate = new Date();
            const formattedDate = currentDate.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric' });
            doc.text(formattedDate, doc.internal.pageSize.width - 40, 40, { align: 'right' });
        };

        drawCommonHeader(doc, reportLocationData);

        // Fetch data
        let rawFetchedData = [];
        if (apiType) {
            try {
                const location = `${provinceName}_${municipalName}_${barangayName}`
                const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
                const response = await fetch(apiUrl, {
                  method: 'GET',
                  headers: {
                    'x-api-key': process.env.REACT_APP_API_KEY
                  }
                });
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    rawFetchedData = data;
                } else {
                    console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                    rawFetchedData = [];
                }
            } catch (error) {
                console.error(`Error fetching data for ${formName}:`, error);
                rawFetchedData = [];
            }
        }

        // --- Table 1: Basic Information ---
        const head1 = [
            ['Purok', 'Household Head Name', 'Name', 'Sex', 'Age', 'Birthdate', 'Level ID', 'Schooling Status']
        ];

        const body1 = rawFetchedData.map(item => [
            item.purok || '',
            item.head_name || '',
            item.name || '',
            item.sex || '',
            item.age || '',
            item.birthdate ? new Date(item.birthdate).toLocaleDateString() : '',
            item.level_id || '',
            item.schooling_status || ''
        ]);

        autoTable(doc, {
            startY: 60, // Start below header
            head: head1,
            body: body1,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fontSize: 8,
            },
            columnStyles: {
                0: { halign: 'center' },  // Purok
                4: { halign: 'center' }, // age
            },
            margin: { top: 60, right: 40, bottom: 60, left: 40 },
            didDrawPage: (data) => {
                // Redraw header on subsequent pages of Table 1
                if (data.pageNumber > 1) {
                    drawCommonHeader(doc, reportLocationData);
                }
            }
        });

        // Add a new page for the second part of the table
        // doc.addPage();
        // drawCommonHeader(doc, reportLocationData); // Draw header on the new page

        // // --- Table 2: Educational Information ---
        // const head2 = [
        //     ['Age', 'Birthdate', 'Level ID', 'Schooling Status']
        // ];

        // const body2 = rawFetchedData.map(item => [
        //     item.age || '',
        //     item.birthdate ? new Date(item.birthdate).toLocaleDateString() : '',
        //     item.level_id || '',
        //     item.schooling_status || ''
        // ]);

        // autoTable(doc, {
        //     startY: 60, // Start below header on the new page
        //     head: head2,
        //     body: body2,
        //     theme: 'grid',
        //     styles: {
        //         fontSize: 8,
        //         cellPadding: 2,
        //         overflow: 'linebreak',
        //         lineWidth: 0.5,
        //         lineColor: [0, 0, 0],
        //         textColor: [0, 0, 0],
        //     },
        //     headStyles: {
        //         fillColor: [255, 255, 255],
        //         textColor: [0, 0, 0],
        //         halign: 'center',
        //         valign: 'middle',
        //         lineWidth: 0.5,
        //         lineColor: [0, 0, 0],
        //         fontSize: 8,
        //     },
        //     columnStyles: {
        //         0: { cellWidth: 50 },  // Age
        //         1: { cellWidth: 120 }, // Birthdate
        //         2: { cellWidth: 120 }, // Level ID
        //         3: { cellWidth: 40 },  // Schooling Status
        //     },
        //     margin: { top: 10, right: 40, bottom: 80, left: 40 },
        //     didDrawPage: (data) => {
        //         // Redraw header on subsequent pages of Table 2
        //         if (data.pageNumber > 1) { // This will apply to pages after the first page of Table 2
        //             drawCommonHeader(doc, reportLocationData);
        //         }
        //     }
        // });

        // Footer (placed after the last table)
        // Page numbering for all pages
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.text(`Page ${i}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height - 40, { align: 'center' });
        }
    } else if (formName === 'MHO Comprehensive Survey Form') {
    doc = new jsPDF("landscape", "pt", "A4"); // Using landscape for more columns

    // Fetch data for MHO Comprehensive Survey Form
    let rawFetchedData = [];
    if (apiType) {
        try {
            const location = `${provinceName}_${municipalName}_${barangayName}`
            const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
            const response = await fetch(apiUrl, {
              method: 'GET',
              headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
              }
            });
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
                rawFetchedData = data;
            } else {
                console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
                rawFetchedData = [];
            }
        } catch (error) {
            console.error(`Error fetching data for ${formName}:`, error);
            rawFetchedData = [];
        }
    }

    // Group data by purok
    const groupedByPurok = rawFetchedData.reduce((acc, current) => {
        const purok = current.purok;
        if (!acc[purok]) {
            acc[purok] = [];
        }
        acc[purok].push(current);
        return acc;
    }, {});

    // Function to draw the common header (main title and barangay/municipality)
    const drawMainHeader = (doc, reportLocationData) => {
        const { barangayName, municipalName } = reportLocationData;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`COMPREHENSIVE SURVEY FORM YEAR _______`, doc.internal.pageSize.width / 2, 40, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Barangay: ${barangayName}`, 40, 60);
        doc.text(`Municipality: ${municipalName}`, doc.internal.pageSize.width / 2, 60, { align: 'center' });
    };

    // Table Headers (defined once as they are consistent across puroks)
    const tableHeaders = [
        [
            'HH No',
            'Name of Family Member',
            'Age',
            'Sex',
            'Civil Status',
            'Birthday',
            'Education Attainment',
            'Occupation',
            'Religion',
            'FP', // Family Planning
            'OPT', // Operation Timbang
            'HH Access to Sanitary Toilet',
            'Water Level',
            'Salt Iodization',
            'Remarks'
        ]
    ];

    let isFirstPurok = true;
    for (const purokValue of Object.keys(groupedByPurok).sort()) { // Sort puroks for consistent order
        if (!isFirstPurok) {
            doc.addPage(); // Add a new page for each new purok
        }
        isFirstPurok = false;

        // Draw main header and Purok text on the first page of each purok's section
        // Note: This will be redrawn by didDrawPage for consistency on all pages
        drawMainHeader(doc, reportLocationData);
        doc.setFontSize(10);
        doc.text(`Purok: ${purokValue}`, 40, 80); // Position below main header

        // Data for the current purok
        const currentPurokData = groupedByPurok[purokValue];
        const body = currentPurokData.map(item => [
            item.hh_no || '',
            item.name || '',
            item.age || '',
            item.sex || '',
            item.civil_status || '',
            item.birthdate ? new Date(item.birthdate).toLocaleDateString() : '', // Format date
            item.level_id || '', // Education Attainment (assuming level_id maps to this)
            item.occupation || '',
            item.religion || '',
            item.fp || '',
            item.opt || '',
            item.toilet || '', // HH Access to Sanitary Toilet
            item.water || '', // Water Level
            item.salt || '', // Salt Iodization
            item.remarks || ''
        ]);

        autoTable(doc, {
            startY: 90, // Start table below "Purok: " text
            head: tableHeaders,
            body: body,
            theme: 'grid',
            styles: {
                fontSize: 7, // Smaller font to fit more columns
                cellPadding: 2,
                overflow: 'linebreak',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [255, 255, 255], // White background for header
                textColor: [0, 0, 0], // Black text for header
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fontSize: 7,
            },
            columnStyles: {
                0: { halign: 'center' }, // HH No
                1: { halign: 'left' },   // Name of Family Member
                2: { cellWidth: 30, halign: 'center' }, // Age
                3: { halign: 'center' }, // Sex
                4: { halign: 'center' }, // Civil Status
                5: { halign: 'center' }, // Birthday
                6: { halign: 'left' },   // Education Attainment
                7: { cellWidth: 90, halign: 'left' }, // Occupation
                8: { halign: 'left' },   // Religion
                9: { cellWidth: 30, halign: 'center' }, // FP
                10: { cellWidth: 30, halign: 'center' }, // OPT
                11: { halign: 'center' }, // HH Access to Sanitary Toilet
                12: { halign: 'center' }, // Water Level
                13: { halign: 'center' }, // Salt Iodization
                14: { cellWidth: 60, halign: 'left' }, // Remarks
            },
            margin: { top: 90, right: 40, bottom: 60, left: 40 }, // Adjust bottom margin for footer
            didDrawPage: (data) => {
                drawMainHeader(doc, reportLocationData);
                doc.setFontSize(10);
                doc.text(`Purok: ${purokValue}`, 40, 80);
            }
        });
    }

    // Footer (applied to all pages after all content is drawn)
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Date Printed: ${formattedDate}`, 40, doc.internal.pageSize.height - 20);
        doc.text(`Page ${i} of ${totalPages}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 20, { align: 'right' });
    }
} else if (apiType) {
      // Existing fetch logic for other forms (default to portrait if not specified)
      doc = new jsPDF("portrait", "pt", "A4");

      // Add header information for other forms
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 40, { align: 'center' });
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text(`Barangay: ${barangayName}, ${municipalName}, ${provinceName}`, doc.internal.pageSize.width / 2, 55, { align: 'center' });
      doc.text(`Form: ${formName}`, doc.internal.pageSize.width / 2, 70, { align: 'center' });

      try {
        const location = `${provinceName}_${municipalName}_${barangayName}`
        const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, Response: ${errorText}`);
          }
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            fetchedData.head = [Object.keys(data[0])]; // Store headers for autoTable
            fetchedData.body = data.map(item => Object.values(item).map(value => value || '0')); // Store body for autoTable
          } else {
            console.warn(`API for ${formName} returned an empty array or non-array data. Data received:`, data);
            fetchedData.head = [['No Data Available']];
            fetchedData.body = [];
          }
        } catch (error) {
          console.error(`Error fetching data for ${formName}:`, error);
          fetchedData.head = [['Error fetching data.']];
          fetchedData.body = [];
        }

      // AutoTable configuration for other forms (existing)
      autoTable(doc, {
        startY: 90,
        head: fetchedData.head,
        body: fetchedData.body,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 8,
        },
      });
    } else {
      // Fallback for invalid form type (existing)
      doc = new jsPDF("portrait", "pt", "A4"); // Default to portrait

      // Add header information for fallback
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 40, { align: 'center' });
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text(`Barangay: ${barangayName}, ${municipalName}, ${provinceName}`, doc.internal.pageSize.width / 2, 55, { align: 'center' });
      doc.text(`Form: ${formName}`, doc.internal.pageSize.width / 2, 70, { align: 'center' });

      fetchedData.head = [['Invalid Form Type']];
      fetchedData.body = [];
      autoTable(doc, {
        startY: 90,
        head: fetchedData.head,
        body: fetchedData.body,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 8,
        },
      });
    }

    return doc;
  };

  const handleViewDownload = async (actionType) => {
    if (!isSearchApplied) {
      alert('Please click "Apply Search" first to enable report actions.');
      return;
    }
    if (!selectedAdministrativeForm) {
      alert(`Please select a form before ${actionType}.`);
      return;
    }

    // locationData is already available from props
    const doc = await generateAdminReport(selectedAdministrativeForm, locationData);
    const {provinceName, municipalName, barangayName } = locationData; // Redundant destructuring but harmless

    if (actionType === 'view') {
      doc.output("dataurlnewwindow");
    } else if (actionType === 'download') {
      const filename = `${barangayName}_${municipalName}_${provinceName}`
      doc.save(`${selectedAdministrativeForm.replace(/\s+/g, '_')}_${filename.replace(/\s+/g, '_')}.pdf`);
    }
  };

  const administrativeFormOptions = [
    'MDG Data Capture Form',
    'Data Aggregation Form',
    'BCPC Form 2',
    'Registered Barangay Inhabitants',
    'RBI Form A',
    'RBI Form B',
    'Barangay Governance Management System',
    'Form No. CMP-03-A',
    'Literacy Report Form',
    'Information on education by age and sex',
    'MHO Comprehensive Survey Form'
  ];

  const disabledButtonStyles = {
    backgroundColor: '#cccccc',
    cursor: 'not-allowed',
    color: '#666666'
  };

  return (
    <div style={pageContainer}>
      <div style={reportContainer}>
        <h2 style={heading}>Generate Administrative Reports</h2>

        <div style={reportCardsContainer}>
          <div style={{ ...card, flex: '1 1 100%', maxWidth: 'calc(100% - 40px)' }}>
            <h3>Administrative Forms</h3>
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="administrativeFormSelect" style={{ marginRight: '10px', fontSize: '16px' }}>Select Form:</label>
              <select
                id="administrativeFormSelect"
                value={selectedAdministrativeForm}
                onChange={(e) => setSelectedAdministrativeForm(e.target.value)}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', width: '250px' }}
                disabled={!isSearchApplied}
              >
                <option value="">-- Select a Form --</option>
                {administrativeFormOptions.map((formName, idx) => (
                  <option key={idx} value={formName}>{formName}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => handleViewDownload('view')}
              style={{
                ...button,
                marginRight: "10px",
                ...(!isSearchApplied || !selectedAdministrativeForm ? disabledButtonStyles : {})
              }}
              disabled={!isSearchApplied || !selectedAdministrativeForm}
            >
              View Selected
            </button>
            <button
              onClick={() => handleViewDownload('download')}
              style={{
                ...button,
                ...(!isSearchApplied || !selectedAdministrativeForm ? disabledButtonStyles : {})
              }}
              disabled={!isSearchApplied || !selectedAdministrativeForm}
            >
              Download Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Styling (reused for consistency)
const pageContainer = {
  minHeight: "calc(100vh - 0px)",
  backgroundColor: "#f8f9fa",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
};

const reportContainer = {
  width: "100%",
  backgroundColor: "#ffffff",
  padding: "30px",
  borderRadius: "10px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
};

const heading = {
  fontSize: "24px",
  fontWeight: "bold",
  marginBottom: "20px",
  textAlign: "center",
  color: "#333",
};

const reportCardsContainer = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "20px",
};

const card = {
  marginBottom: "20px",
  padding: "15px",
  border: "1px solid #ddd",
  borderRadius: "8px",
  backgroundColor: "#fefefe",
  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
  flex: "1 1 calc(33% - 40px)",
  minWidth: "280px",
  maxWidth: "calc(100% - 40px)", // Adjusted to be full width within its container
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

const button = {
  padding: "10px 20px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
  transition: 'background-color 0.3s ease',
};

// disabledButtonStyles is already defined and used
// const disabledButtonStyles = {
//   backgroundColor: '#cccccc',
//   cursor: 'not-allowed',
//   color: '#666666',
// };

export default AdministrativeReports;